import { useCallback, useEffect, useMemo, useState } from "react";
import { isEqual } from "lodash";
import {
  Button,
  Group,
  LoadingOverlay,
  Radio,
  Stack,
  TextInput,
  NumberInput,
  ActionIcon,
  Title,
  Breadcrumbs,
  Anchor,
  Select,
} from "@mantine/core";
import { zodResolver } from "mantine-form-zod-resolver";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDebug } from "common/debug/hooks/useDebug";
import {
  useAddPlaceMutation,
  useUpdatePlaceMutation,
} from "place/api/place.api";
import { PlaceFormProvider, usePlaceForm } from "place/context/place.context";
import { placeSchema } from "place/admin/components/forms/PlaceSchema";
import Collapsable from "common/admin/ui/Collapsable";

import type { Place } from "place/types/place.model";
import { CirclePlus, Copy, Trash } from "tabler-icons-react";
import { ADMIN_PATH } from "_urls";
import {
  ADMIN_ORGANIZATION,
  ORGANIZATIONS,
  PLACE_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  PLACE_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
} from "_roles";
import { useOrganization } from "common/admin/auth/hooks";
import { v4 as uuidv4 } from "uuid";
import { AccessRights } from "common/admin/auth/AccessRights";

export function PlaceAdminForm({
  place,
  editMode,
}: {
  place?: Place;
  editMode: boolean;
}) {
  const { isDebugMode, log } = useDebug();
  const location = useLocation();
  const navigate = useNavigate();

  const [addPlace, addPlaceResult] = useAddPlaceMutation();
  const [updatePlace, updatePlaceResult] = useUpdatePlaceMutation();
  const [isLoading, setIsLoading] = useState(false);

  const [organization] = useOrganization();
  const isDuplicate = location.pathname.endsWith("/duplicate");

  useEffect(() => {
    if (isDebugMode) {
      if (addPlaceResult.isError) {
        log(`Add error: ${JSON.stringify(addPlaceResult.error)}`);
      }
      if (updatePlaceResult.isError) {
        log(`Update error: ${JSON.stringify(updatePlaceResult.error)}`);
      }
    }
  }, [isDebugMode, addPlaceResult, updatePlaceResult, log]);

  const getInitialValues = useCallback(() => {
    if (place && editMode) {
      return {
        partitionId: place.partitionId || "",
        parentId: isDuplicate ? place.id : "", // 👈 Handle parentId logic here
        name: place.name || "",
        status: place.status as "available" | "unavailable",
        type: place.type as "common" | "bookmarked",
        creationTime: place.creationTime || new Date().toISOString(),
        owner: {
          L1: place.owner?.L1 || organization || "",
          L2: place.owner?.L2 || "",
          L3: place.owner?.L3 || "",
          L4: place.owner?.L4 || "",
        },
        detailedSettings: {
          subPlaces: isDuplicate
            ? []
            : place.detailedSettings?.subPlaces?.map((sp, i) => ({
                ...sp,
                id: sp.id || uuidv4(),
                index: i + 1,
              })) || [],
        },
      };
    }

    return {
      partitionId: "",
      parentId: "",
      name: "",
      status: "available" as "available" | "unavailable",
      type: "common" as "common" | "bookmarked",
      creationTime: new Date().toISOString(),
      owner: {
        L1: organization || "",
        L2: "",
        L3: "",
        L4: "",
      },
      detailedSettings: {
        subPlaces: [],
      },
    };
  }, [place, editMode, isDuplicate, organization]);
  const form = usePlaceForm({
    initialValues: getInitialValues(),
    validate: zodResolver(placeSchema),
  });

  const isDirty = useMemo(
    () => !isEqual(form.values, getInitialValues()),
    [form.values, getInitialValues],
  );

  const handleAddSubPlace = () => {
    const current = form.values.detailedSettings.subPlaces;

    if (
      current.length > 0 &&
      (!current[current.length - 1].title ||
        !current[current.length - 1].numberOfWinners ||
        !current[current.length - 1].status)
    ) {
      return;
    }

    const newSubPlaces = [
      ...current,
      {
        id: uuidv4(),

        title: "",
        numberOfWinners: 1,
        status: "enabled",
      },
    ];

    // Ensure indexes are sequential
    form.setFieldValue(
      "detailedSettings.subPlaces",
      newSubPlaces.map((sp, i) => ({ ...sp, index: i + 1 })),
    );
  };

  const handleDuplicateSubPlace = (indexToDuplicate: number) => {
    const subPlace = form.values.detailedSettings.subPlaces[indexToDuplicate];

    // Validate required fields before duplicating
    if (
      !subPlace?.title?.trim() ||
      !subPlace?.numberOfWinners ||
      !subPlace?.status
    ) {
      // You can optionally show a notification or log a message here
      log("Cannot duplicate: SubPlace has incomplete fields");
      return;
    }

    const duplicated = {
      ...subPlace,
      id: uuidv4(),
      index: form.values.detailedSettings.subPlaces.length + 1,
      title: `${subPlace.title}`, // optional tweak
    };

    const newSubPlaces = [...form.values.detailedSettings.subPlaces];
    newSubPlaces.splice(indexToDuplicate + 1, 0, duplicated);

    // Re-index all
    const reIndexed = newSubPlaces.map((sp, i) => ({ ...sp, index: i + 1 }));
    form.setFieldValue("detailedSettings.subPlaces", reIndexed);
  };

  const handleRemoveSubPlace = (indexToRemove: number) => {
    const updated = form.values.detailedSettings.subPlaces
      .filter((_, i) => i !== indexToRemove)
      .map((sp, i) => ({ ...sp, index: i + 1 }));

    form.setFieldValue("detailedSettings.subPlaces", updated);
  };

  const save = async () => {
    console.log("Saving place", form.values);
    const result = form.validate();
    if (!result.hasErrors) {
      const transformed = form.values;
      console.log("Saving place", transformed);
      setIsLoading(true);

      try {
        if (place && editMode && !isDuplicate) {
          await updatePlace({ id: place.id, ...transformed });
        } else {
          await addPlace(transformed).unwrap();
        }
        navigate(`../`);
      } catch (error) {
        log("Error saving place", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      log("Validation failed", result.errors);
    }
  };

  return (
    <>
      {isLoading && <LoadingOverlay visible={true} overlayOpacity={0.8} />}
      <PlaceFormProvider form={form}>
        <Stack>
          <div
            className="sticky top-0 z-50 pb-4"
            style={{ backgroundColor: "#E9ECEF" }}
          >
            <Title order={3} align="center" m="xs">
              {place?.name || "New Place"}
            </Title>
            <Breadcrumbs separator="→">
              <Anchor component={Link} to={`/${ADMIN_PATH}`}>
                Admin
              </Anchor>

              <Anchor component={Link} to={`/${ADMIN_PATH}/placeadmin`}>
                Places
              </Anchor>
            </Breadcrumbs>
            <Group position="right">
              <Button
                disabled={!isDirty}
                onClick={save}
                className="bg-button-admin disabled:opacity-75"
              >
                Save
              </Button>
            </Group>
          </div>
          <Group className="grid grid-cols-2">
            {organization === ADMIN_ORGANIZATION && (
              <AccessRights
                authorizedOrganizations={
                  PLACE_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
                }
                authorizedRoles={PLACE_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES}
              >
                <Select
                  sx={{ flex: 1 }}
                  label="Organisation"
                  required
                  data={ORGANIZATIONS}
                  {...form.getInputProps("owner.L1")}
                />
              </AccessRights>
            )}
            {/* <TextInput label="L2" {...form.getInputProps("owner.L2")} />
              <TextInput label="L3" {...form.getInputProps("owner.L3")} />
              <TextInput label="L4" {...form.getInputProps("owner.L4")} /> */}
          </Group>
          <Group className="grid grid-cols-1 grid-rows-2">
            <TextInput
              label="Name"
              required
              {...form.getInputProps("name")}
              className="w-[50%]"
            />

            <Radio.Group label="Status" {...form.getInputProps("status")}>
              <Group mt="xs">
                <Radio value="available" label="Available" />
                <Radio value="unavailable" label="Unavailable" />
              </Group>
            </Radio.Group>

            {/* <TextInput label="Type" required {...form.getInputProps("type")} /> */}
          </Group>

          {form.values.detailedSettings.subPlaces.map((subPlace, index) => (
            <div key={index}>
              <Collapsable
                title={`Sub Place # ${index + 1}`}
                actions={
                  <Group spacing="xs">
                    <ActionIcon
                      onClick={() => handleDuplicateSubPlace(index)}
                      disabled={
                        !subPlace.title?.trim() ||
                        !subPlace.numberOfWinners ||
                        !subPlace.status
                      }
                    >
                      <Copy size={20} color="blue" />
                    </ActionIcon>

                    <ActionIcon onClick={() => handleRemoveSubPlace(index)}>
                      <Trash color="red" size={20} />
                    </ActionIcon>
                  </Group>
                }
              >
                <Group className="grid grid-rows-2 grid-cols-1">
                  <Group className="grid grid-rows-1 grid-cols-3">
                    <TextInput
                      label="Title"
                      {...form.getInputProps(
                        `detailedSettings.subPlaces.${index}.title`,
                      )}
                      className="col-span-2"
                      required
                    />
                    <NumberInput
                      label="No of Winners"
                      min={1}
                      {...form.getInputProps(
                        `detailedSettings.subPlaces.${index}.numberOfWinners`,
                      )}
                    />
                  </Group>
                  <TextInput
                    label="SubPlace ID"
                    disabled
                    {...form.getInputProps(
                      `detailedSettings.subPlaces.${index}.id`,
                    )}
                  />

                  <Radio.Group
                    label="Status"
                    {...form.getInputProps(
                      `detailedSettings.subPlaces.${index}.status`,
                    )}
                  >
                    <Radio value="enabled" label="Enabled" />
                    <Radio value="disabled" label="Disabled" />
                  </Radio.Group>
                </Group>
              </Collapsable>
            </div>
          ))}

          <Group position="center" align="center">
            <Button
              onClick={handleAddSubPlace}
              variant="subtle"
              leftIcon={<CirclePlus />}
              disabled={
                form.values.detailedSettings.subPlaces.length > 0 &&
                (!form.values.detailedSettings.subPlaces
                  .at(-1)
                  ?.title?.trim() ||
                  !form.values.detailedSettings.subPlaces.at(-1)
                    ?.numberOfWinners ||
                  !form.values.detailedSettings.subPlaces.at(-1)?.status)
              }
            >
              Add SubPlace
            </Button>
          </Group>
        </Stack>
      </PlaceFormProvider>
    </>
  );
}
